import { DestroyRef, inject, Injectable, Injector, Signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { PmsSyncStatus } from '@anduin/api';
import { getLoadedResults, Logger } from '@anduin/shared';
import { ConfigService, PmSystem } from '@anduin/config';
import { Store } from '@ngxs/store';
import { defer, EMPTY, filter, repeat, Subject, Subscription, switchMap, take, takeUntil, tap, timer } from 'rxjs';
import { EmailDeliveryHubState, LoadPmsSyncStatus, UpdatePmsSyncPollingState } from '../state';
import { PmsSyncPollingState } from '../state';

const POLLING_INTERVAL_MS = 30_000; // 30 seconds between polls during an active sync window
const MAX_POLLING_TRIES = 10; // give up after 5 minutes (10 × 30s); dispatch UNCONFIRMED if exceeded

@Injectable()
export class EmailDeliveryHubPmsSyncService {
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly logger = inject(Logger);
  private readonly configService = inject(ConfigService);
  private readonly injector = inject(Injector);

  private pollingSubscription: Subscription | null = null;
  private scheduledSyncTimeout: ReturnType<typeof setTimeout> | null = null;
  private stopPolling$ = new Subject<void>();
  private initialized = false;
  private isUnsentTabActive: Signal<boolean>;
  private readyToRefresh = false;
  private initialLoadDone = false;

  init(isUnsentTabActive: Signal<boolean>): void {
    if (this.initialized || this.configService.pmSystem !== PmSystem.CCH_AXCESS) {
      return;
    }

    this.initialized = true;
    this.isUnsentTabActive = isUnsentTabActive;

    toObservable(isUnsentTabActive, { injector: this.injector })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(Boolean),
      )
      .subscribe(() => {
        if (!this.initialLoadDone) {
          this.initialLoadDone = true;
          this.store.dispatch(new LoadPmsSyncStatus());
          this.waitForInitialLoad();
        } else if (this.readyToRefresh) {
          this.readyToRefresh = false;
          this.startPolling(this.getPreviousLastSyncedAt());
        }
      });
  }

  destroy(): void {
    this.cleanup();
    this.initialized = false;
    this.readyToRefresh = false;
    this.initialLoadDone = false;
  }

  private waitForInitialLoad(): void {
    getLoadedResults(this.store.select(EmailDeliveryHubState.getPmsSyncStatus), true)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        tap((syncStatus) => this.handleInitialLoad(syncStatus)),
      )
      .subscribe();
  }

  private getPreviousLastSyncedAt(): number | null {
    return this.store.selectSnapshot(EmailDeliveryHubState.getPmsSyncStatus)?.data?.lastSyncedAt ?? null;
  }

  private handleInitialLoad(syncStatus: PmsSyncStatus | undefined): void {
    if (!syncStatus?.syncIntervalMs) {
      this.logger.warn('EmailDeliveryHubPmsSyncService :: syncIntervalMs unavailable — cannot schedule polling');
      return;
    }

    if (!syncStatus.nextSyncEstimatedAt) {
      this.logger.warn('EmailDeliveryHubPmsSyncService :: nextSyncEstimatedAt unavailable — cannot schedule polling');
      return;
    }

    const delay = syncStatus.nextSyncEstimatedAt - Date.now();

    if (delay <= 0) {
      // The expected sync window has already elapsed — the sync may be in progress right now
      this.startPolling(syncStatus.lastSyncedAt);
    } else {
      this.scheduleNextPoll(delay, syncStatus.lastSyncedAt);
    }
  }

  private scheduleNextPoll(delayMs: number, previousLastSyncedAt: number | null): void {
    this.logger.info(`EmailDeliveryHubPmsSyncService :: Scheduling next poll in ${delayMs}ms`);

    this.scheduledSyncTimeout = setTimeout(() => {
      if (this.isUnsentTabActive()) {
        this.startPolling(previousLastSyncedAt);
      } else {
        this.logger.info('EmailDeliveryHubPmsSyncService :: Poll due but not on unsent tab, marking ready to refresh');
        this.readyToRefresh = true;
      }
    }, delayMs);
  }

  /**
   * Begins polling the PMS sync status endpoint at POLLING_INTERVAL_MS intervals.
   *
   * We compare each response's `lastSyncedAt` against `previousLastSyncedAt`:
   * - Changed → outcome 1: sync completed with new data.
   * - Unchanged after MAX_POLLING_TRIES → outcome 2 or 3: either the sync is still running,
   *   or it completed but ingested no new data. These are indistinguishable from this endpoint,
   *   so we surface an UNCONFIRMED state to the UI with explanatory tooltip copy.
   * - API error → outcome 4: an unexpected failure; we surface an ERROR state and schedule a retry.
   */
  private startPolling(previousLastSyncedAt: number | null): void {
    if (this.pollingSubscription) {
      return;
    }

    this.logger.info('EmailDeliveryHubPmsSyncService :: Starting polling for PMS sync');
    this.store.dispatch(new UpdatePmsSyncPollingState(PmsSyncPollingState.POLLING));
    this.stopPolling$ = new Subject<void>();
    let attempts = 0;

    this.pollingSubscription = defer(() => {
      if (!this.isUnsentTabActive()) {
        this.logger.info('EmailDeliveryHubPmsSyncService :: Unsent tab no longer active, deferring sync');
        this.readyToRefresh = true;
        this.stopPolling();
        return EMPTY;
      }

      attempts++;
      this.store.dispatch(new LoadPmsSyncStatus(true));
      return getLoadedResults(this.store.select(EmailDeliveryHubState.getPmsSyncStatus), true).pipe(take(1));
    }).pipe(
      switchMap((syncStatus: PmsSyncStatus | undefined) => {
        if (!syncStatus) {
          // getLoadedResults emits undefined when the loadable is in ERROR state
          this.logger.error('EmailDeliveryHubPmsSyncService :: API error fetching PMS sync status during polling');
          this.onPollingError();
          return EMPTY;
        }

        const syncedWithNewData = syncStatus.lastSyncedAt !== null && syncStatus.lastSyncedAt !== previousLastSyncedAt;

        if (syncedWithNewData) {
          this.onSyncDetected(syncStatus);
          return EMPTY;
        }

        if (attempts >= MAX_POLLING_TRIES) {
          this.logger.warn(
            `EmailDeliveryHubPmsSyncService :: Max polling attempts (${MAX_POLLING_TRIES}) reached — sync unconfirmed`,
          );
          this.onPollingUnconfirmed(syncStatus);
          return EMPTY;
        }

        return timer(POLLING_INTERVAL_MS);
      }),
      repeat(),
      takeUntil(this.stopPolling$),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  /** Outcome 1: a new lastSyncedAt was detected — sync completed with new data. */
  private onSyncDetected(syncStatus: PmsSyncStatus): void {
    this.logger.info('EmailDeliveryHubPmsSyncService :: PMS sync detected — new lastSyncedAt received');
    this.stopPolling();
    this.store.dispatch(new UpdatePmsSyncPollingState(PmsSyncPollingState.SYNCED));
    this.scheduleNextCycle(syncStatus);
  }

  /**
   * Outcomes 2 & 3: polling timed out with no new lastSyncedAt.
   * We cannot distinguish between a sync that is still running and one that completed with no new data.
   * The UNCONFIRMED state is surfaced to the UI so the user can see the ambiguity.
   * We resume scheduling so the next sync window will be polled again normally.
   */
  private onPollingUnconfirmed(syncStatus: PmsSyncStatus): void {
    this.stopPolling();
    this.store.dispatch(new UpdatePmsSyncPollingState(PmsSyncPollingState.UNCONFIRMED));
    this.scheduleNextCycle(syncStatus);
  }

  /** Outcome 4: an API error occurred mid-poll. Reschedule after syncIntervalMs (or a fallback). */
  private onPollingError(): void {
    this.stopPolling();
    this.store.dispatch(new UpdatePmsSyncPollingState(PmsSyncPollingState.ERROR));

    const snapshot = this.store.selectSnapshot(EmailDeliveryHubState.getPmsSyncStatus);
    const retryDelayMs = snapshot?.data?.syncIntervalMs ?? POLLING_INTERVAL_MS;
    const previousLastSyncedAt = snapshot?.data?.lastSyncedAt ?? null;

    this.logger.info(`EmailDeliveryHubPmsSyncService :: Scheduling error-recovery poll in ${retryDelayMs}ms`);
    this.scheduleNextPoll(retryDelayMs, previousLastSyncedAt);
  }

  /**
   * Schedules the next polling cycle using the nextSyncEstimatedAt from the latest response.
   * Falls back to now + syncIntervalMs if nextSyncEstimatedAt is absent or already in the past
   * (which can happen when the sync completed but ingested no new data and the backend hasn't
   * advanced the estimated time yet).
   */
  private scheduleNextCycle(syncStatus: PmsSyncStatus): void {
    if (!syncStatus.syncIntervalMs) {
      return;
    }

    const nextSyncTime = syncStatus.nextSyncEstimatedAt
      ? syncStatus.nextSyncEstimatedAt
      : Date.now() + syncStatus.syncIntervalMs;

    const delay = Math.max(0, nextSyncTime - Date.now());

    if (delay === 0) {
      // nextSyncEstimatedAt is in the past (backend hasn't updated it yet); advance by a full interval
      this.scheduleNextPoll(syncStatus.syncIntervalMs, syncStatus.lastSyncedAt);
    } else {
      this.scheduleNextPoll(delay, syncStatus.lastSyncedAt);
    }
  }

  private stopPolling(): void {
    this.stopPolling$.next();
    this.stopPolling$.complete();
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = null;
  }

  private cleanup(): void {
    this.stopPolling();

    if (this.scheduledSyncTimeout) {
      clearTimeout(this.scheduledSyncTimeout);
      this.scheduledSyncTimeout = null;
    }
  }
}
